import { DialogCloseEvent } from '@theme/dialog';
import { clamp, preventDefault } from '@theme/utilities';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const DEFAULT_ZOOM = 1;
const DOUBLE_TAP_ZOOM = 2.5;
const DOUBLE_CLICK_ZOOM = 2.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;
const SINGLE_CLICK_DELAY = 250;
const WHEEL_INTENSITY = 0.0018;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/**
 * A custom element that lets the shopper zoom and pan a single media item inside
 * the fullscreen zoom dialog, using touch gestures (pinch, double-tap, drag) on
 * mobile and mouse input (wheel, double-click, click-drag) on desktop.
 *
 * @extends {Component<Refs>}
 */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {boolean} */
  #isMouseDown = false;

  /** @type {number | null} */
  #singleClickTimer = null;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const touchOptions = { passive: false, signal };

    // Touch input (mobile): pinch to zoom, double-tap to zoom, single-finger drag to pan.
    this.addEventListener('touchstart', this.#handleTouchStart, touchOptions);
    this.addEventListener('touchmove', this.#handleTouchMove, touchOptions);
    this.addEventListener('touchend', this.#handleTouchEnd, touchOptions);

    // Mouse/trackpad input (desktop): wheel to zoom at cursor, double-click to zoom at
    // point, click-drag to pan, plain click to close (unless a drag just happened).
    this.addEventListener('wheel', this.#handleWheel, { passive: false, signal });
    this.addEventListener('dblclick', this.#handleDblClick, { signal });
    this.addEventListener('mousedown', this.#handleMouseDown, { signal });
    this.addEventListener('click', this.#handleClick, { signal });

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    window.removeEventListener('mousemove', this.#handleMouseMove);
    window.removeEventListener('mouseup', this.#handleMouseUp);
    if (this.#singleClickTimer) clearTimeout(this.#singleClickTimer);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // Use performance.now() for better precision and performance
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Early exit if too much time has passed
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      // Only check distance if we have a previous tap within time window
      if (this.#lastTapPosition) {
        // Distance calculation with early exit
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          // This is a double-tap, handle zoom toggle
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0; // Reset to prevent triple-tap
          this.#lastTapPosition = null;
          return;
        }
      }

      // Store tap info for potential double-tap detection
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle double-tap zoom toggle from touch
   * @param {Touch} touch - The touch where the double-tap occurred
   */
  #handleDoubleTapFromTouch(touch) {
    let targetZoom;

    // If manual zoom has been used, reset to 1x
    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM; // 1x
      this.#hasManualZoom = false; // Reset the flag
      this.#translate = { x: 0, y: 0 }; // Center the image
    } else {
      // Toggle between zoom levels: 1x <-> DOUBLE_TAP_ZOOM
      const tolerance = 0.05; // Small tolerance for floating point comparison

      if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
        // Currently at 1x, zoom in on the tapped point
        targetZoom = DOUBLE_TAP_ZOOM;
      } else {
        // Currently zoomed in, go back to 1x
        targetZoom = MIN_ZOOM;
      }
    }

    // If we're not going to 1x, adjust translation to center zoom on the tap point
    if (targetZoom !== MIN_ZOOM) {
      this.#applyZoomAtPoint(Math.min(MAX_ZOOM, targetZoom), touch.clientX, touch.clientY);
      this.#hasManualZoom = true;
    } else {
      // Going to 1x, set the scale and center the image
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 }; // Center the image when going to 1x
    }

    this.#requestUpdateTransform();
    this.#notifyZoomChange();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);

    // Calculate and apply new scale, anchored on the midpoint between the fingers
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#applyZoomAtPoint(clamp(newScale, MIN_ZOOM, MAX_ZOOM), midX, midY);

    // Mark that manual zoom has been used
    this.#hasManualZoom = true;

    this.#requestUpdateTransform();
    this.#notifyZoomChange();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    // Check if we've moved beyond the drag threshold
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();

      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * Zoom in or out, keeping the given viewport point (in client coordinates) stationary.
   * Shared by wheel, double-click, double-tap and pinch gestures.
   *
   * @param {number} newScale - The target scale, already clamped by the caller.
   * @param {number} clientX - The viewport X coordinate to zoom around.
   * @param {number} clientY - The viewport Y coordinate to zoom around.
   */
  #applyZoomAtPoint(newScale, clientX, clientY) {
    const oldScale = this.#scale;
    if (newScale === oldScale) return;

    const rect = this.getBoundingClientRect();
    const containerCenterX = rect.width / 2;
    const containerCenterY = rect.height / 2;

    const pointX = clientX - rect.left;
    const pointY = clientY - rect.top;

    const distanceFromCenterX = pointX - containerCenterX;
    const distanceFromCenterY = pointY - containerCenterY;

    this.#scale = newScale;

    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;
  }

  /**
   * Mouse wheel / trackpad: zoom in and out around the cursor position.
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    event.preventDefault();

    const zoomFactor = Math.exp(-event.deltaY * WHEEL_INTENSITY);
    const newScale = clamp(this.#scale * zoomFactor, MIN_ZOOM, MAX_ZOOM);
    if (newScale === this.#scale) return;

    this.#applyZoomAtPoint(newScale, event.clientX, event.clientY);
    this.#hasManualZoom = newScale > MIN_ZOOM;

    this.#requestUpdateTransform();
    this.#notifyZoomChange();
  };

  /**
   * Double-click: toggle between 1x and a useful zoom level, anchored at the click point.
   * @param {MouseEvent} event
   */
  #handleDblClick = (event) => {
    event.preventDefault();

    if (this.#singleClickTimer) {
      clearTimeout(this.#singleClickTimer);
      this.#singleClickTimer = null;
    }

    const isZoomedIn = this.#scale > MIN_ZOOM + 0.05;

    if (isZoomedIn) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#hasManualZoom = false;
    } else {
      this.#applyZoomAtPoint(Math.min(MAX_ZOOM, DOUBLE_CLICK_ZOOM), event.clientX, event.clientY);
      this.#hasManualZoom = true;
    }

    this.#requestUpdateTransform();
    this.#notifyZoomChange();
  };

  /**
   * Mouse button down: start a potential pan. Actual panning only moves the image
   * once the scale is above 1x; at 1x this just tracks the gesture so a genuine
   * click-to-close still works normally.
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (event.button !== 0) return; // left button only

    event.preventDefault(); // avoid native image drag/ghost and text selection

    this.#startPosition = { x: event.clientX, y: event.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isMouseDown = true;
    this.#hasDraggedBeyondThreshold = false;

    window.addEventListener('mousemove', this.#handleMouseMove);
    window.addEventListener('mouseup', this.#handleMouseUp);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#isMouseDown) return;

    const distance = getDistance(event, this.#startPosition);
    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) return;

    this.#hasDraggedBeyondThreshold = true;
    this.classList.add('is-panning');

    if (this.#scale <= MIN_ZOOM) return; // nothing to pan at 1x

    const dx = event.clientX - this.#startPosition.x;
    const dy = event.clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseUp = (event) => {
    this.#isMouseDown = false;
    this.classList.remove('is-panning');
    window.removeEventListener('mousemove', this.#handleMouseMove);
    window.removeEventListener('mouseup', this.#handleMouseUp);
  };

  /**
   * Click: closes the surrounding zoom dialog, unless this click is the tail end of a
   * drag/pan gesture, or turns out to be the first half of a double-click (in which case
   * the pending close is cancelled by #handleDblClick).
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (this.#hasDraggedBeyondThreshold) {
      this.#hasDraggedBeyondThreshold = false;
      event.stopPropagation();
      return;
    }

    if (this.#singleClickTimer) return;

    this.#singleClickTimer = setTimeout(() => {
      this.#singleClickTimer = null;
      if (this.#scale > MIN_ZOOM + 0.001) return; // ignore plain clicks while zoomed in

      const dialog = /** @type {{ close?: () => void }} */ (this.closest('zoom-dialog'));
      dialog?.close?.();
    }, SINGLE_CLICK_DELAY);
  };

  /**
   * Notify ancestors (e.g. the zoom toolbar) that the scale of this instance changed.
   */
  #notifyZoomChange() {
    this.dispatchEvent(
      new CustomEvent('dragzoom:change', {
        bubbles: true,
        detail: { scale: this.#scale },
      })
    );
  }

  /**
   * Zoom in/out by a fixed step, anchored at the center of the image. Used by the +/- buttons.
   * @param {number} delta
   */
  zoomBy(delta) {
    const rect = this.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const newScale = clamp(this.#scale + delta, MIN_ZOOM, MAX_ZOOM);
    this.#applyZoomAtPoint(newScale, centerX, centerY);
    this.#hasManualZoom = newScale > MIN_ZOOM;
    this.#requestUpdateTransform();
    this.#notifyZoomChange();
  }

  /**
   * @returns {number} the current scale.
   */
  getScale() {
    return this.#scale;
  }

  /**
   * Publicly resets zoom/pan back to the default state. Used by the "Zurücksetzen" button
   * and whenever the shopper switches to a different photo.
   */
  reset() {
    this.#resetZoom();
    this.#notifyZoomChange();
  }

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    // Get wrapper dimensions
    const wrapperRect = this.getBoundingClientRect();

    // Calculate ACTUAL image content dimensions at current zoom
    // The image element may fill the wrapper, but the content has its own aspect ratio
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    // Try to get natural dimensions
    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // Fallback: assume square image if we can't get natural dimensions
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    // Calculate how the image fits within the wrapper (object-fit: contain behavior)
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider - width fits exactly, height is smaller (letterboxed top/bottom)
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      // Image is taller - height fits exactly, width is smaller (letterboxed left/right)
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    // Apply current zoom scale
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    // SIMPLE APPROACH: Calculate constraints directly from image content dimensions
    // If image content is larger than wrapper, calculate max translation directly

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    // Max translation is half the overflow (since image starts centered)
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    // Apply symmetric constraints (object-fit: contain behavior)
    // Image starts centered, can move maxTranslate in each direction
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    this.classList.toggle('is-zoomed', this.#scale > MIN_ZOOM);
  };

  /**
   * Reset zoom to default state (1x scale, centered position)
   * Called when zoom is exited/closed, or when the shopper switches to another photo.
   */
  #resetZoom = () => {
    // Reset scale and translation to defaults
    this.#scale = DEFAULT_ZOOM;
    this.#startScale = DEFAULT_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#isMouseDown = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#hasManualZoom = false;

    // Update CSS properties to reflect reset state
    this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
    this.classList.remove('is-zoomed', 'is-panning');
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch | MouseEvent} point1 - First point, touch or mouse event
 * @param {Point | Touch | MouseEvent} point2 - Second point, touch or mouse event
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch/MouseEvent objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
